/**
 * LazyThumbnail - Thumbnail that only loads when visible.
 * Uses IntersectionObserver for visibility detection.
 * Thumbnails have text/drawings already baked in by the server.
 */

import { useEffect, useRef, useState, memo } from 'react';
import { thumbnailLoader } from '../services/thumbnailLoader';
import { thumbhashUrl } from '../utils/thumbhashUrl';

interface LazyThumbnailProps {
  itemId: number;
  alt?: string;
  className?: string;
  priority?: number;
  /** FEAT-012: ~25-byte blur of the real image — paints before any fetch. */
  thumbhash?: string | null;
}

// Placeholder SVG (gray square)
const PLACEHOLDER = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23222" width="100" height="100"/></svg>';

// Error placeholder (gray with ?)
const ERROR_PLACEHOLDER = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23333" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23666" font-size="30">?</text></svg>';

function LazyThumbnailComponent({
  itemId,
  alt = '',
  className = '',
  priority = 0,
  thumbhash,
}: LazyThumbnailProps) {
  const blurPlaceholder = thumbhashUrl(thumbhash);
  const containerRef = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Check cache immediately (sync) for instant display
  useEffect(() => {
    const cached = thumbnailLoader.getCached(itemId);
    if (cached) {
      setSrc(cached);
    }
  }, [itemId]);

  // Set up IntersectionObserver for visibility detection
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Only need to observe once
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before visible
        threshold: 0,
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Load thumbnail when visible
  useEffect(() => {
    if (!isVisible || src) return;

    let mounted = true;

    thumbnailLoader.load(itemId, priority).then((url) => {
      if (mounted) {
        if (url) {
          setSrc(url);
        } else {
          setHasError(true);
        }
      }
    });

    return () => {
      mounted = false;
    };
  }, [isVisible, itemId, priority, src]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <img
        src={hasError ? ERROR_PLACEHOLDER : (src || blurPlaceholder || PLACEHOLDER)}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${
          src || blurPlaceholder ? 'opacity-100' : 'opacity-50'
        }`}
        loading="lazy"
        onError={() => setHasError(true)}
      />
      {/* Loading shimmer effect when not yet loaded */}
      {!src && !hasError && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer-sweep" />
      )}
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export const LazyThumbnail = memo(LazyThumbnailComponent);
